import React, { useState } from "react";
import { Button } from "../../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../../design/dialog";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
  Description,
} from "../../../../design/fieldset";
import { Select } from "../../../../design/select";
import { Input } from "../../../../design/input";
import { useAttorney } from "../../../../contexts/AttorneyContext";
import { useCase } from "../../../../contexts/CaseContext";
import {
  AttorneyRole,
  ATTORNEY_ROLE_LABELS,
  CaseAttorney,
} from "@visitproof/types";
import { Checkbox, CheckboxField, CheckboxGroup } from "../../../../design/checkbox";
import { PencilIcon } from "@heroicons/react/16/solid";
import { formatDateUTC, parseDateToTimestamp } from "../../../../utils/date";

interface Props {
  assignment: CaseAttorney;
  onUpdated: (updatedAssignment: CaseAttorney) => void;
}

const ROLE_OPTIONS: { value: AttorneyRole; label: string }[] = [
  { value: "visiting_parent_attorney", label: ATTORNEY_ROLE_LABELS.visiting_parent_attorney },
  { value: "custodial_parent_attorney", label: ATTORNEY_ROLE_LABELS.custodial_parent_attorney },
  { value: "petitioner_attorney", label: ATTORNEY_ROLE_LABELS.petitioner_attorney },
  { value: "respondent_attorney", label: ATTORNEY_ROLE_LABELS.respondent_attorney },
  { value: "childs_attorney", label: ATTORNEY_ROLE_LABELS.childs_attorney },
  { value: "gal", label: ATTORNEY_ROLE_LABELS.gal },
  { value: "minors_counsel", label: ATTORNEY_ROLE_LABELS.minors_counsel },
  { value: "casa", label: ATTORNEY_ROLE_LABELS.casa },
  { value: "mediator", label: ATTORNEY_ROLE_LABELS.mediator },
  { value: "other", label: ATTORNEY_ROLE_LABELS.other },
];

interface Errors {
  role?: string;
  roleLabel?: string;
  representedParentId?: string;
}

const EditAttorneyAssignmentButton: React.FC<Props> = ({
  assignment,
  onUpdated,
}) => {
  const { updateCaseAttorney } = useAttorney();
  const { parents, childList } = useCase();

  const [isOpen, setIsOpen] = useState(false);
  const [role, setRole] = useState<AttorneyRole>(assignment.role);
  const [roleLabel, setRoleLabel] = useState(assignment.roleLabel || "");
  const [representedParentId, setRepresentedParentId] = useState<string>(
    assignment.representedParentId || ""
  );
  const [representedChildIds, setRepresentedChildIds] = useState<string[]>(
    assignment.representedChildIds || []
  );
  const [startDate, setStartDate] = useState<string>(
    formatDateUTC(assignment.startDate, "input")
  );
  const [endDate, setEndDate] = useState<string>(
    formatDateUTC(assignment.endDate, "input")
  );
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  const isParentRole =
    role === "visiting_parent_attorney" ||
    role === "custodial_parent_attorney" ||
    role === "petitioner_attorney" ||
    role === "respondent_attorney";
  const isChildRole =
    role === "childs_attorney" ||
    role === "gal" ||
    role === "minors_counsel" ||
    role === "casa";

  const handleOpen = () => {
    // Reset form to current assignment values
    setRole(assignment.role);
    setRoleLabel(assignment.roleLabel || "");
    setRepresentedParentId(assignment.representedParentId || "");
    setRepresentedChildIds(assignment.representedChildIds || []);
    setStartDate(formatDateUTC(assignment.startDate, "input"));
    setEndDate(formatDateUTC(assignment.endDate, "input"));
    setErrors({});
    setIsOpen(true);
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSaving(true);
    try {
      const updateData: Partial<CaseAttorney> = {
        role,
        roleLabel: role === "other" ? roleLabel : undefined,
        representedParentId: isParentRole ? representedParentId || undefined : undefined,
        representedChildIds: isChildRole && representedChildIds.length > 0 ? representedChildIds : undefined,
        startDate: parseDateToTimestamp(startDate),
        endDate: parseDateToTimestamp(endDate),
      };

      await updateCaseAttorney(assignment.id, updateData);

      // Return the updated assignment
      onUpdated({
        ...assignment,
        ...updateData,
      });

      setIsOpen(false);
    } catch (error) {
      console.error("Error updating attorney assignment:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const validate = (): boolean => {
    const newErrors: Errors = {};

    if (!role) {
      newErrors.role = "Please select a role";
    }

    if (role === "other" && !roleLabel.trim()) {
      newErrors.roleLabel = "Please specify the role";
    }

    // Only require parent selection if there are parents to select from
    if (isParentRole && parents.length > 0 && !representedParentId) {
      newErrors.representedParentId = "Please select which parent this attorney represents";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChildToggle = (childId: string, checked: boolean) => {
    if (checked) {
      setRepresentedChildIds((prev) => [...prev, childId]);
    } else {
      setRepresentedChildIds((prev) => prev.filter((id) => id !== childId));
    }
  };

  return (
    <>
      <Button plain onClick={handleOpen} title="Edit assignment">
        <PencilIcon className="h-4 w-4" />
      </Button>
      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>Edit Attorney Assignment</DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              {/* Role Selection */}
              <Field>
                <Label>Role</Label>
                <Select
                  value={role}
                  onChange={(e) => {
                    setRole(e.target.value as AttorneyRole);
                    // Clear parent/child selections when role changes
                    setRepresentedParentId("");
                    setRepresentedChildIds([]);
                  }}
                >
                  {ROLE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                {errors.role && <ErrorMessage>{errors.role}</ErrorMessage>}
              </Field>

              {/* Custom Role Label (for "other") */}
              {role === "other" && (
                <Field>
                  <Label>Role Description</Label>
                  <Input
                    type="text"
                    value={roleLabel}
                    onChange={(e) => setRoleLabel(e.target.value)}
                    placeholder="e.g., Co-counsel, Expert witness..."
                  />
                  {errors.roleLabel && (
                    <ErrorMessage>{errors.roleLabel}</ErrorMessage>
                  )}
                </Field>
              )}

              {/* Parent Selection (for parent-related attorney roles) */}
              {isParentRole && (
                <Field>
                  <Label>Represents</Label>
                  <Description>
                    Which parent does this attorney represent?
                  </Description>
                  {parents.length > 0 ? (
                    <Select
                      value={representedParentId}
                      onChange={(e) => setRepresentedParentId(e.target.value)}
                    >
                      <option value="">Select a parent...</option>
                      {parents.map((parent) => (
                        <option key={parent.id} value={parent.id}>
                          {parent.firstName} {parent.lastName}
                          {parent.role ? ` (${parent.role})` : ""}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <p className="text-sm text-zinc-500 dark:text-zinc-400 italic">
                      No parents have been added to this case yet.
                    </p>
                  )}
                  {errors.representedParentId && (
                    <ErrorMessage>{errors.representedParentId}</ErrorMessage>
                  )}
                </Field>
              )}

              {/* Child Selection (for child-related attorney/advocate roles) */}
              {isChildRole && (
                <Field>
                  <Label>Represents</Label>
                  <Description>
                    Which child(ren) does this attorney/advocate represent?
                  </Description>
                  {childList.length > 0 ? (
                    <CheckboxGroup className="mt-2">
                      {childList.map((child) => (
                        <CheckboxField key={child.id}>
                          <Checkbox
                            checked={representedChildIds.includes(child.id)}
                            onChange={(checked) => handleChildToggle(child.id, checked)}
                          />
                          <Label>
                            {child.firstName} {child.lastName}
                          </Label>
                        </CheckboxField>
                      ))}
                    </CheckboxGroup>
                  ) : (
                    <p className="text-sm text-zinc-500 dark:text-zinc-400 italic">
                      No children have been added to this case yet.
                    </p>
                  )}
                </Field>
              )}

              {/* Representation Dates */}
              <div className="grid grid-cols-2 gap-4">
                <Field>
                  <Label>Start Date</Label>
                  <Description>When representation began</Description>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </Field>
                <Field>
                  <Label>End Date</Label>
                  <Description>When representation ended (leave blank if ongoing)</Description>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </Field>
              </div>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditAttorneyAssignmentButton;
